import type { ComponentStyleConfig } from '@chakra-ui/theme';
import { Colors, FontSizes, LineHeights, Radii, Shadows, Space, TextStyles } from '../../types';
import { defineStyle } from '@chakra-ui/react';
import { type StyleFunctionProps } from '@chakra-ui/theme-tools';

const disabledStyles = {
  base: {
    opacity: 1,
    cursor: 'not-allowed',
    color: Colors.TEXT_DISABLED,
    bg: Colors.SURFACE_DISABLED,
    boxShadow: 'none',
  },
  solid: {
    bg: Colors.SURFACE_DISABLED,
    color: Colors.TEXT_DISABLED,
    borderColor: Colors.TRANSPARENT,
  },
  outline: {
    bg: Colors.TRANSPARENT,
    color: Colors.TEXT_DISABLED,
    borderColor: Colors.SURFACE_DISABLED,
  },
  ghost: {
    bg: Colors.TRANSPARENT,
    color: Colors.TEXT_DISABLED,
    borderColor: Colors.TRANSPARENT,
  },
  invert: {
    bg: 'color-mix(in srgb, var(--udl-colors-surface-tertiary-invert) 50%, transparent)', // TODO: update disabled colors to gray so we don't need to do this
    color: 'color-mix(in srgb, var(--udl-colors-text-subdued-invert) 50%, transparent)',
  },
  invertOutline: {
    bg: Colors.TRANSPARENT,
    color: 'color-mix(in srgb, var(--udl-colors-text-subdued-invert) 50%, transparent)',
    borderColor: Colors.CURRENT,
  },
  invertGhost: {
    bg: Colors.TRANSPARENT,
    color: 'color-mix(in srgb, var(--udl-colors-text-subdued-invert) 50%, transparent)',
    borderColor: Colors.TRANSPARENT,
  },
};

const solidFocusStyles = {
  outline: '2px solid',
  outlineColor: Colors.BORDER_FOCUS,
  outlineOffset: '1px',
  boxShadow: 'none',
};

const baseStyle = defineStyle({
  background: Colors.SURFACE_PAGE,
  border: '1px solid',
  borderColor: Colors.TRANSPARENT,
  borderRadius: Radii.MD,
  display: 'inline-flex',
  gap: '.25em',
  transitionProperty: 'common',
  transitionTimingFunction: 'ease',
  transitionDuration: 'fast',
  '.chakra-button__icon': {
    // Nullify Chakra's `iconSpacing` prop that attaches to this wrapper, otherwise we can't set `gap` per button size
    // Then make its height match the text's, so icons stay aligned regardless of fontSize
    m: 0,
    alignSelf: 'center',
    fontSize: '1em',
    minH: '1lh',
    verticalAlign: 'middle',
    display: 'inline-flex',
    alignItems: 'center',
    _empty: {
      display: 'none',
    },
  },
  'svg.fa-icon': {
    // Fallback for Foundry buttons that have icons w/o above wrapper
    margin: '0',
    fontSize: '1em',
    '--fa-fw-width': '1em', // unset .fa-fw class to help optical alignment
  },
  '.btn-text': {
    _empty: {
      display: 'none',
    },
  },
  _hover: {
    _disabled: {
      ...disabledStyles.base,
    },
  },
  _disabled: {
    ...disabledStyles.base,
    _hover: {
      ...disabledStyles.base,
    },
    img: { filter: 'grayscale(100%)', opacity: '0.5' },
  },
});

const variants = {
  solid: (props: StyleFunctionProps) => {
    const { colorScheme, isInvert } = props;
    if (isInvert) {
      return {
        color: Colors.TEXT_EMPHASIS_INVERT,
        bg: Colors.SURFACE_TERTIARY_INVERT,
        borderColor: Colors.TRANSPARENT,
        _hover: {
          color: Colors.TEXT_EMPHASIS_INVERT,
          bg: Colors.SURFACE_TERTIARY_INVERT_HOVER,
          borderColor: Colors.TRANSPARENT,
          _disabled: { ...disabledStyles.invert },
        },
        _active: {
          color: Colors.TEXT_EMPHASIS_INVERT,
          bg: Colors.SURFACE_TERTIARY_INVERT_ACTIVE,
          borderColor: Colors.TRANSPARENT,
        },
        _focusVisible: {
          ...solidFocusStyles,
        },
        _disabled: { ...disabledStyles.invert },
      };
    }
    if (colorScheme === 'gray' || colorScheme === 'neutral') {
      return {
        bg: Colors.SURFACE_TERTIARY,
        color: Colors.TEXT_EMPHASIS,
        borderColor: Colors.TRANSPARENT,
        _hover: {
          bg: Colors.SURFACE_TERTIARY_HOVER,
          _disabled: { ...disabledStyles.solid },
        },
        _active: {
          bg: Colors.SURFACE_TERTIARY_ACTIVE,
        },
        _focusVisible: {
          ...solidFocusStyles,
        },
        _disabled: { ...disabledStyles.solid },
      };
    }
    if (colorScheme === 'brand') {
      return {
        bg: Colors.SURFACE_PRIMARY,
        color: Colors.TEXT_ON_PRIMARY,
        borderColor: Colors.TRANSPARENT,
        _hover: {
          bg: Colors.SURFACE_PRIMARY_HOVER,
          color: Colors.TEXT_ON_PRIMARY_HOVER,
          _disabled: { ...disabledStyles.solid },
        },
        _active: {
          bg: Colors.SURFACE_PRIMARY_ACTIVE,
          color: Colors.TEXT_ON_PRIMARY_ACTIVE,
        },
        _focusVisible: {
          ...solidFocusStyles,
        },
        _disabled: {
          ...disabledStyles.solid,
        },
      };
    }
    return {
      bg: `${props.colorScheme}.500`,
      color: Colors.WHITE,
      borderColor: Colors.TRANSPARENT,
      _hover: {
        bg: `${props.colorScheme}.600`,
        _disabled: { ...disabledStyles.solid },
      },
      _active: {
        bg: `${props.colorScheme}.700`,
      },
      _dark: {
        color: `${props.colorScheme}.25`,
        bg: `${props.colorScheme}.700`,
        _hover: { bg: `${props.colorScheme}.600` },
        _active: { bg: `${props.colorScheme}.500` },
      },
      _focusVisible: {
        ...solidFocusStyles,
      },
      _disabled: {
        ...disabledStyles.solid,
      },
    };
  },
  outline: function (props: StyleFunctionProps) {
    const { colorScheme, isInvert } = props;
    if (isInvert) {
      return {
        color: Colors.TEXT_EMPHASIS_INVERT,
        bg: Colors.TRANSPARENT,
        borderColor: Colors.BORDER_INVERT,
        _hover: {
          bg: Colors.SURFACE_TERTIARY_INVERT_HOVER,
          _disabled: {
            ...disabledStyles.invertOutline,
          },
        },
        _active: {
          bg: Colors.SURFACE_TERTIARY_INVERT_ACTIVE,
        },
        _disabled: { ...disabledStyles.invertOutline },
      };
    }
    if (colorScheme === 'gray' || colorScheme === 'neutral') {
      return {
        color: Colors.TEXT_EMPHASIS,
        bg: Colors['WHITEALPHA.25'],
        borderColor: Colors.BORDER_DEFAULT,
        _hover: {
          bg: Colors.SURFACE_HOVER,
          _disabled: {
            ...disabledStyles.outline,
          },
        },
        _active: {
          bg: Colors.SURFACE_ACTIVE,
          borderColor: Colors.BORDER_MUTED,
        },
        _disabled: {
          ...disabledStyles.outline,
        },
      };
    }
    if (colorScheme === 'brand') {
      return {
        color: Colors.TEXT_ON_SECONDARY,
        bg: Colors.SURFACE_SECONDARY,
        borderColor: Colors.BORDER_BRAND,
        _hover: {
          color: Colors.TEXT_ON_SECONDARY_HOVER,
          bg: Colors.SURFACE_SECONDARY_HOVER,
          _disabled: {
            ...disabledStyles.outline,
          },
        },
        _active: {
          color: Colors.TEXT_ON_SECONDARY_ACTIVE,
          bg: Colors.SURFACE_SECONDARY_ACTIVE,
          borderColor: Colors.SURFACE_BRAND,
        },
        _disabled: {
          ...disabledStyles.outline,
        },
      };
    }
    return {
      bg: Colors['WHITEALPHA.25'],
      borderColor: `${props.colorScheme}.200`,
      color: `${props.colorScheme}.600`,
      _dark: {
        color: `${props.colorScheme}.300`,
        _hover: {
          color: `${props.colorScheme}.200`,
        },
        _active: {
          color: `${props.colorScheme}.100`,
        },
      },
      _hover: {
        _disabled: {
          ...disabledStyles.outline,
        },
      },
      _disabled: {
        ...disabledStyles.outline,
      },
    };
  },
  ghost: function (props: StyleFunctionProps) {
    const { colorScheme, isInvert } = props;
    if (isInvert) {
      return {
        color: Colors.TEXT_EMPHASIS_INVERT,
        bg: Colors.TRANSPARENT,
        borderColor: Colors.TRANSPARENT,
        _hover: {
          color: Colors.TEXT_EMPHASIS_INVERT,
          bg: Colors.SURFACE_TERTIARY_INVERT_HOVER,
          _disabled: { ...disabledStyles.invertGhost },
        },
        _active: {
          color: Colors.TEXT_EMPHASIS_INVERT,
          bg: Colors.SURFACE_TERTIARY_INVERT_ACTIVE,
        },
        _disabled: { ...disabledStyles.invertGhost },
      };
    }
    if (colorScheme === 'gray' || colorScheme === 'neutral') {
      return {
        color: Colors.TEXT_EMPHASIS,
        borderColor: Colors.TRANSPARENT,
        bg: Colors.TRANSPARENT,
        _hover: {
          bg: Colors.SURFACE_TERTIARY_HOVER,
          color: Colors.TEXT_EMPHASIS,
          _disabled: {
            ...disabledStyles.ghost,
          },
        },
        _active: {
          bg: Colors.SURFACE_TERTIARY_ACTIVE,
          color: Colors.TEXT_EMPHASIS,
          borderColor: Colors.TRANSPARENT,
        },
        _disabled: {
          ...disabledStyles.ghost,
        },
      };
    }
    if (colorScheme === 'brand') {
      return {
        color: Colors.TEXT_INTERACTIVE,
        bg: Colors.TRANSPARENT,
        _hover: {
          color: Colors.TEXT_INTERACTIVE_HOVER,
          bg: Colors.SURFACE_TERTIARY,
          _disabled: {
            ...disabledStyles.ghost,
          },
        },
        _active: {
          color: Colors.TEXT_INTERACTIVE_ACTIVE,
          bg: Colors.SURFACE_TERTIARY_HOVER,
        },
        _disabled: { ...disabledStyles.ghost },
      };
    }
    return {
      color: `${props.colorScheme}.600`,
      bg: Colors.TRANSPARENT,
      boxShadow: Shadows.NONE,
      borderColor: Colors.TRANSPARENT,
      _hover: {
        bg: Colors.SURFACE_TERTIARY,
        _disabled: {
          ...disabledStyles.ghost,
        },
      },
      _active: {
        color: `${props.colorScheme}.700`,
        bg: Colors.SURFACE_TERTIARY_HOVER,
      },
      _disabled: { ...disabledStyles.ghost },
      _dark: {
        color: `${props.colorScheme}.300`,
        _hover: { color: `${props.colorScheme}.200` },
        _active: { color: `${props.colorScheme}.100` },
      },
    };
  },
  elevated: {
    bg: Colors.SURFACE_DIALOG,
    borderColor: Colors.TRANSPARENT,
    outline: '1px solid',
    outlineColor: Colors.BORDER_ELEVATED,
    color: Colors.TEXT_EMPHASIS,
    boxShadow: Shadows.XS,
    _hover: {
      bg: Colors.SURFACE_DIALOG_HOVER,
      outline: '1px solid',
      outlineColor: Colors.BORDER_ELEVATED,
      boxShadow: Shadows.XS,
      _disabled: { ...disabledStyles.outline },
    },
    _active: {
      bg: Colors.SURFACE_DIALOG_ACTIVE,
      boxShadow: Shadows.XS,
    },
    _disabled: { ...disabledStyles.outline },
    _dark: {
      outlineOffset: '-1px',
    },
  },
  link: (props: StyleFunctionProps) => {
    const { colorScheme } = props;

    if (colorScheme === 'gray' || colorScheme === 'neutral') {
      return {
        bg: Colors.TRANSPARENT,
        color: Colors.TEXT_EMPHASIS,
        borderColor: Colors.TRANSPARENT,
        _hover: {
          color: Colors.TEXT_EMPHASIS,
          textDecoration: 'underline',
          _disabled: {
            ...disabledStyles.ghost,
          },
        },
        _active: {
          color: Colors.TEXT_EMPHASIS,
        },
        _disabled: {
          ...disabledStyles.ghost,
        },
      };
    }
    return {
      color: `${props.colorScheme}.600`,
      bg: Colors.TRANSPARENT,
      _hover: {
        textDecoration: 'underline',
        color: `${props.colorScheme}.700`,
        _disabled: {
          ...disabledStyles.ghost,
        },
      },
      _active: { color: `${props.colorScheme}.800` },
      _dark: {
        color: `${props.colorScheme}.300`,
        _hover: { color: `${props.colorScheme}.200` },
        _active: { color: `${props.colorScheme}.100` },
      },
      _disabled: { ...disabledStyles.ghost },
    };
  },
  primary: (props: StyleFunctionProps) =>
    props.theme.components.Button.variants.solid({
      ...props,
      variant: 'solid',
      colorScheme: 'brand',
    }),
  secondary: (props: StyleFunctionProps) =>
    props.theme.components.Button.variants.outline({
      ...props,
      variant: 'outline',
      colorScheme: 'brand',
    }),
  tertiary: (props: StyleFunctionProps) =>
    props.theme.components.Button.variants.ghost({
      ...props,
      variant: 'ghost',
      colorScheme: 'brand',
    }),
  neutral: (props: StyleFunctionProps) =>
    props.theme.components.Button.variants.solid({
      ...props,
      variant: 'solid',
      colorScheme: 'gray',
    }),
  destructive: (props: StyleFunctionProps) =>
    props.theme.components.Button.variants.solid({
      ...props,
      variant: 'solid',
      colorScheme: 'error',
    }),
};

const buttonSizes = {
  '2xs': defineStyle({
    borderRadius: Radii.SM,
    px: Space.XS,
    py: Space['2XS'],
    gap: Space['2XS'],
    h: Space.SPACE_6, // 24px = utility / nested buttons
    minW: Space.SPACE_6,
    fontSize: FontSizes.SM,
    lineHeight: LineHeights.LINE_HEIGHTS_5,
  }),
  xs: defineStyle({
    borderRadius: Radii.BASE,
    px: Space.SM,
    py: Space.XS,
    gap: Space['2XS'],
    h: Space.SPACE_7, // 28px = Base compact
    minW: Space.SPACE_7,
    fontSize: FontSizes.SM,
    lineHeight: LineHeights.LINE_HEIGHTS_5,
  }),
  sm: defineStyle({
    borderRadius: Radii.MD,
    px: Space.XS,
    py: Space.XS,
    gap: Space.SPACE_1_5,
    minW: Space.SPACE_8, // 32px = Dense default, Spacious compact
    h: Space.SPACE_8,
    '&': { apply: `textStyles.${TextStyles.UI_DEFAULT}` },
  }),
  md: defineStyle({
    borderRadius: Radii.LG,
    py: Space.SPACE_2_5,
    px: 'calc(var(--udl-space-lg) / 2)',
    gap: Space.XS,
    minW: Space.SPACE_10, // 40px = Base default
    h: 'calc(var(--udl-space-md) * 2)',
    fontSize: FontSizes.SM,
    lineHeight: LineHeights.LINE_HEIGHTS_5,
    '&': { apply: `textStyles.${TextStyles.UI_DEFAULT}` },
  }),
  lg: defineStyle({
    borderRadius: Radii.XL,
    py: Space.SM,
    px: Space.MD,
    gap: Space.XS,
    minW: Space.SPACE_12, // 48px = Spacious default
    h: Space.SPACE_12,
    fontSize: FontSizes.MD,
    lineHeight: LineHeights.LINE_HEIGHTS_5,
  }),
};

export const Button: ComponentStyleConfig = {
  baseStyle,
  sizes: {
    ...buttonSizes,
    compact: {
      // TODO: make this assignment themeable (or subject to a density layerStyle)
      ...buttonSizes.sm,
      px: 'calc(var(--udl-space-md) / 2)',
      '&': { apply: `textStyles.${TextStyles.UI_COMPACT}` },
    },
  },
  variants,
  defaultProps: {
    variant: 'outline',
    colorScheme: 'gray',
    size: 'md',
    iconSpacing: '0',
  },
};
